import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Ruler, CalendarDays, Mail, Phone, User } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { ContactForm } from "../components/ContactForm";
import { ShareButtons } from "../components/ShareButtons";

const Fact = ({ icon: Icon, label, value, testId }) =>
  value ? (
    <div className="p-5 border-b border-white/10" data-testid={testId}>
      <p className="label-xs flex items-center gap-2"><Icon size={12} className="text-primary" />{label}</p>
      <p className="mt-2 text-lg">{value}</p>
    </div>
  ) : null;

const Gallery = ({ photos, name }) => {
  const [active, setActive] = useState(0);
  if (!photos.length) {
    return <div className="aspect-[4/5] bg-muted grid place-items-center text-white/20"><User size={96} /></div>;
  }
  return (
    <div data-testid="profile-gallery">
      <div className="aspect-[4/5] overflow-hidden bg-muted">
        <img src={fileUrl(photos[active].path)} alt={name} className="w-full h-full object-cover" data-testid="profile-main-photo" />
      </div>
      {photos.length > 1 && (
        <div className="grid grid-cols-4 gap-2 mt-2">
          {photos.map((p, i) => (
            <button key={p.id} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border-2 transition-colors ${i === active ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`profile-thumb-${i}`}>
              <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PublicContact = ({ extra }) => (
  <div className="border border-white/10 p-6 space-y-4" data-testid="profile-public-contact">
    <p className="label-xs">Contact directly</p>
    {extra.public_email && (
      <a href={`mailto:${extra.public_email}`} className="flex items-center gap-3 hover:text-primary transition-colors" data-testid="profile-public-email">
        <Mail size={16} className="text-primary" />{extra.public_email}
      </a>
    )}
    {extra.public_phone && (
      <a href={`tel:${extra.public_phone}`} className="flex items-center gap-3 hover:text-primary transition-colors" data-testid="profile-public-phone">
        <Phone size={16} className="text-primary" />{extra.public_phone}
      </a>
    )}
  </div>
);

export default function ExtraProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [extra, setExtra] = useState(undefined);

  useEffect(() => {
    api.get(`/extras/${slug.toLowerCase()}`).then((r) => setExtra(r.data)).catch(() => setExtra(null));
  }, [slug]);

  useEffect(() => {
    if (extra && window.location.pathname !== `/${extra.slug}`) {
      navigate(`/${extra.slug}`, { replace: true });
    }
  }, [extra, navigate]);

  if (extra === undefined) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;
  if (extra === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="profile-not-found">
        <h1 className="font-display text-4xl font-bold">Profile not available</h1>
        <p className="text-white/60 mt-4">This profile doesn't exist or its listing has expired.</p>
        <Link to="/search" className="inline-block mt-8 text-primary uppercase font-bold text-sm tracking-wider">Back to search</Link>
      </div>
    );
  }

  const age = extra.playing_age_min != null && extra.playing_age_max != null ? `${extra.playing_age_min} – ${extra.playing_age_max}` : null;
  const location = [extra.town, extra.region, extra.country && extra.country !== "United Kingdom" ? extra.country : ""].filter(Boolean).join(", ") + (extra.postcode_area ? ` (${extra.postcode_area})` : "");

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="extra-profile-page">
      <Link to="/search" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors" data-testid="profile-back-link">
        <ArrowLeft size={16} /> Back to search
      </Link>
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-8">
        <div className="md:col-span-5 fade-up"><Gallery photos={extra.photos} name={extra.display_name} /></div>
        <div className="md:col-span-7 fade-up" style={{ animationDelay: "120ms" }}>
          <p className="label-xs text-primary">{extra.gender || "Extra"}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-none" data-testid="profile-name">{extra.display_name}</h1>
          <div className="grid sm:grid-cols-3 border-t border-white/10 mt-8">
            <Fact icon={MapPin} label="Location" value={location.trim()} testId="profile-location" />
            <Fact icon={CalendarDays} label="Playing age" value={age} testId="profile-age" />
            <Fact icon={Ruler} label="Height" value={extra.height_cm ? `${extra.height_cm} cm` : null} testId="profile-height" />
          </div>
          {extra.bio && <p className="mt-8 text-white/80 leading-relaxed whitespace-pre-line" data-testid="profile-bio">{extra.bio}</p>}
          {extra.skills?.length > 0 && (
            <div className="mt-8">
              <p className="label-xs">Skills & attributes</p>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-skills">
                {extra.skills.map((s) => <span key={s} className="border border-white/15 px-3 py-1.5 text-sm">{s}</span>)}
              </div>
            </div>
          )}
          <div className="mt-12">
            {extra.contact_mode === "public" && (extra.public_email || extra.public_phone) ? (
              <PublicContact extra={extra} />
            ) : (
              <ContactForm slug={extra.slug} name={extra.display_name} />
            )}
          </div>
          <ShareButtons slug={extra.slug} name={extra.display_name} />
        </div>
      </div>
    </div>
  );
}
